// Hand-authored back-face data for two-sided Sorcery cards.
//
// Two-sided cards in Sorcery. Like the Druid avatar. Have a different
// design printed on the reverse: different art, different rules text,
// often different stats. Upstream Sorcery cards JSON does NOT include
// back-face data, so we maintain it here, keyed by the unified card id
// produced by `normalizeSorceryCard` (e.g. `sorcery-druid`).
//
// What each entry encodes:
//   front:  Optional override for the unified card's primary rules
//           text. We need this for Druid specifically because the
//           upstream `guardian.rulesText` is actually the *post-flip*
//           ("UPDATED") wording, which would otherwise display
//           mismatched against the front-face image we render by
//           default in every grid/picker/inspector.
//   back:   The card data the UI should render when the player flips
//           the card over: name, rules text, type text, stats, plus a
//           per-printing slug mapping. The variant slug suffix is what
//           the Sorcery CDN uses (`-r`). E.g. `art-druid-bt-s` →
//           `art-druid-bt-s-r`. Only printings listed in `variants`
//           have a real back-face image; promotional reprints that
//           already carry the post-flip wording are intentionally
//           omitted because they aren't flippable.
//
// When a second flippable card lands, add another entry here. If/when
// upstream starts shipping back-face data, this file becomes the
// override layer instead of the source of truth.
export const CARD_BACK_FACES = {
  'sorcery-druid': {
    front: {
      rulesText:
        'Tap → Play or draw a site. If this is your first turn, summon Tawny here.\n\n'
        + 'Tap → Summon Bruin here. Flip this card.',
    },
    back: {
      name: 'Druid',
      rulesText:
        'UPDATED: Tap → Play or draw a site.\n\n'
        + 'Nearby allied sites have "Whenever an enemy enters here, it takes 1 damage."',
      typeText: 'Your Avatar is a force of nature!',
      flavorText: '',
      type: 'Avatar',
      attack: 1,
      defence: 1,
      life: 20,
      thresholds: { air: 0, earth: 0, fire: 0, water: 0 },
      slugSuffix: '-r',
      // Standard (Arthurian Legends) + Foil (Promotional) printings both
      // have a real back-face image upstream; the foil back carries the
      // foil treatment. Rainbow is intentionally omitted (no back asset).
      variants: ['art-druid-bt-s', 'pro-druid-d-f'],
    },
  },
};

export function getCardBackFaceDef(cardId) {
  return CARD_BACK_FACES[cardId] || null;
}

// Maps a front-face variant slug to its back-face counterpart, or null
// if the variant doesn't have a back face. Used by the image sync
// scripts so back-face slugs are uploaded alongside front faces.
export function getBackFaceVariantSlug(frontVariantSlug) {
  for (const def of Object.values(CARD_BACK_FACES)) {
    const back = def.back;
    if (back?.variants?.includes(frontVariantSlug)) {
      return frontVariantSlug + back.slugSuffix;
    }
  }
  return null;
}

// Flat list of every back-face slug we know about. Convenience for
// scripts (image sync, image bundle).
export function allBackFaceSlugs() {
  const result = [];
  for (const def of Object.values(CARD_BACK_FACES)) {
    const back = def.back;
    if (!back?.variants) continue;
    for (const front of back.variants) {
      result.push(front + back.slugSuffix);
    }
  }
  return result;
}
